import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EToolTip from 'components/others/EToolTip';

interface TaskTimeProgressCircleProps {
    taskId: number;
    elapsedPercent: number;
    days?: number;
    size?: number;
}

type ViewMode = 'days' | 'percent' | 'none';

const STORAGE_KEY = 'econtractor-task-time-progress-view';

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

const TaskTimeProgressCircle = ({
    taskId,
    elapsedPercent,
    days,
    size = 32,
}: TaskTimeProgressCircleProps) => {
    const { t } = useTranslation(['Tasks']);
    const safeElapsed = clamp(elapsedPercent, 0, 100);
    const progressDeg = safeElapsed * 3.6;
    const hasDays = typeof days === 'number';

    const hue = 120 - (safeElapsed / 100) * 120;
    const color = `hsl(${hue}, 78%, 72%)`;
    const glowColor = `hsla(${hue}, 70%, 55%, 0.18)`;
    const trackColor = 'rgba(255,255,255,0.58)';

    const r = size / 2;
    const cx = r;
    const cy = r;

    const polarToCartesian = (angleDeg: number) => {
        const rad = ((angleDeg - 90) * Math.PI) / 180;
        return {
            x: cx + r * Math.cos(rad),
            y: cy + r * Math.sin(rad),
        };
    };

    const buildSectorPath = (angleDeg: number) => {
        const clamped = clamp(angleDeg, 0, 359.999);
        const start = polarToCartesian(0);
        const end = polarToCartesian(clamped);
        const largeArcFlag = clamped > 180 ? 1 : 0;
        return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
    };

    const sectorPath = buildSectorPath(progressDeg);
    const modes: ViewMode[] = useMemo(
        () => (hasDays ? ['days', 'percent', 'none'] : ['percent', 'none']),
        [hasDays],
    );

    const [mode, setMode] = useState<ViewMode>(() => {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            if (!raw) return hasDays ? 'days' : 'percent';
            const parsed = JSON.parse(raw) as Record<string, ViewMode>;
            const saved = parsed?.[String(taskId)];
            return saved && modes.includes(saved) ? saved : hasDays ? 'days' : 'percent';
        } catch {
            return hasDays ? 'days' : 'percent';
        }
    });

    useEffect(() => {
        if (!modes.includes(mode)) setMode(modes[0]);
    }, [modes, mode]);

    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            const parsed = raw ? (JSON.parse(raw) as Record<string, ViewMode>) : {};
            parsed[String(taskId)] = mode;
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        } catch {
            // ignore
        }
    }, [taskId, mode]);

    const handleToggleMode = (event: React.MouseEvent) => {
        event.stopPropagation();
        setMode((prev) => {
            const idx = modes.indexOf(prev);
            return modes[idx >= 0 ? (idx + 1) % modes.length : 0];
        });
    };

    const tooltipTitle =
        mode === 'percent'
            ? `${t('term.elapsed')}: ${Math.round(safeElapsed)}%`
            : `${t('term.daysLeft')}: ${Math.max(0, Math.round(days || 0))}`;

    return (
        <EToolTip title={tooltipTitle}>
            <button
                type="button"
                className="task-time-progress-circle"
                style={{
                    width: size,
                    height: size,
                    boxShadow: `0 0 0 1px rgba(0,0,0,0.05), 0 2px 8px ${glowColor}`,
                }}
                onClick={handleToggleMode}
            >
                <svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    className="task-time-progress-circle__svg"
                    aria-hidden="true"
                >
                    <circle cx={cx} cy={cy} r={r} fill={trackColor} />
                    {safeElapsed > 0 && <path d={sectorPath} fill={color} />}
                </svg>
                <span className="task-time-progress-circle__glow" />
                <span
                    className={`task-time-progress-circle__label${mode === 'none' ? ' is-empty' : ''}`}
                >
                    {mode === 'days' && hasDays
                        ? `${Math.max(0, Math.round(days || 0))}`
                        : mode === 'percent'
                          ? `${Math.round(safeElapsed)}%`
                          : '0'}
                </span>
            </button>
        </EToolTip>
    );
};

export default TaskTimeProgressCircle;
